import { useMemo, useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Input, InputNumber, Select } from "antd";
import { Button } from "@/shared/components/button";
import { Textarea } from "@/shared/components/dialog";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import {
  CORRECTABLE_FIELDS,
  REASON_MAX_LENGTH,
  decideReviewItemApi,
  type CorrectableField,
  type CorrectedValue,
  type DecisionAction,
  type PhysicianReviewItem,
} from "@/portals/physician/api/reviewQueueApi";

// ── Labels & wording ─────────────────────────────────────────────────────────

export const PAIR_OPTIONS: { action: DecisionAction; label: string }[] = [
  { action: "KEEP_EXISTING", label: "Keep the earlier record" },
  { action: "ACCEPT_NEW", label: "Accept the newer record" },
  { action: "BOTH_VALID", label: "Both are valid" },
  { action: "MARK_DISCONTINUED", label: "Mark one as discontinued" },
  { action: "CORRECT_VALUE", label: "Correct the value" },
  { action: "REJECT_BOTH", label: "Reject both" },
  { action: "REQUEST_MORE_INFO", label: "Ask the patient a question" },
];

export const SINGLE_OPTIONS: { action: DecisionAction; label: string }[] = [
  { action: "KEEP_EXISTING", label: "Confirm this record" },
  { action: "MARK_DISCONTINUED", label: "Mark as discontinued" },
  { action: "CORRECT_VALUE", label: "Correct the value" },
  { action: "REJECT_BOTH", label: "Reject this record" },
  { action: "REQUEST_MORE_INFO", label: "Ask the patient a question" },
];

export function actionLabel(action: DecisionAction, isPair: boolean): string {
  return (isPair ? PAIR_OPTIONS : SINGLE_OPTIONS).find((o) => o.action === action)?.label ?? action;
}

export const MESSAGES = {
  decided: "Decision recorded.",
  questionSent: "Question sent to the patient.",
  notDecidable: "This item can't be decided right now.",
  waiting: "Waiting for the patient's answer",
  reasonLabel: "Reason (the patient will see this)",
  questionLabel: "Your question to the patient",
};

type Role = "EXISTING" | "INCOMING";
const ROLE_WORD: Record<Role, string> = { EXISTING: "earlier", INCOMING: "newer" };
const other = (role: Role): Role => (role === "EXISTING" ? "INCOMING" : "EXISTING");

/** Plain-words confirmation of what the decision will do (the contract's outcome table). */
export function effectSentence(action: DecisionAction, isPair: boolean, targetRole?: Role): string {
  if (action === "REQUEST_MORE_INFO") return "Your question will be sent to the patient.";
  if (!isPair) {
    return {
      KEEP_EXISTING: "This record will be kept.",
      MARK_DISCONTINUED: "This record will be marked as discontinued.",
      CORRECT_VALUE: "This record will be corrected.",
      REJECT_BOTH: "This record will be dismissed.",
    }[action as "KEEP_EXISTING" | "MARK_DISCONTINUED" | "CORRECT_VALUE" | "REJECT_BOTH"] ?? "";
  }
  const t = targetRole ?? "EXISTING";
  switch (action) {
    case "KEEP_EXISTING": return "The earlier record will be kept and the newer record dismissed.";
    case "ACCEPT_NEW": return "The newer record will be kept and the earlier record dismissed.";
    case "BOTH_VALID": return "Both records will be kept.";
    case "MARK_DISCONTINUED": return `The ${ROLE_WORD[t]} record will be marked as discontinued and the ${ROLE_WORD[other(t)]} record kept.`;
    case "CORRECT_VALUE": return `The ${ROLE_WORD[t]} record will be corrected and the ${ROLE_WORD[other(t)]} record dismissed.`;
    case "REJECT_BOTH": return "Both records will be dismissed.";
  }
}

const FIELD_LABELS: Record<CorrectableField, string> = {
  medication_name: "Medication name",
  dose_quantity: "Dose quantity",
  dose_unit: "Dose unit",
  frequency: "Frequency",
  route: "Route",
  dosage_text: "Instructions",
  status: "Status",
};

// FHIR MedicationRequest.status values a physician would set here.
const STATUS_OPTIONS = ["active", "on-hold", "completed", "stopped", "cancelled"];

const BRAND = "!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-5 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";
const OUTLINE = "!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]";

function choiceCardClass(selected: boolean) {
  return cn(
    "!flex !h-auto !w-full !items-center !justify-start !gap-2.5 !whitespace-normal !rounded-xl !px-3 !py-2.5 !text-left !text-sm !font-semibold !text-[#0A2947]",
    selected
      ? "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!border-[#F17893] hover:!bg-[#FFF2F4] hover:!text-[#0A2947]"
      : "!border-[#DDE1E8] !bg-white hover:!border-[#F17893]/50 hover:!bg-white hover:!text-[#0A2947]",
  );
}

function Choice({ selected, label, onClick }: { selected: boolean; label: string; onClick: () => void }) {
  return (
    <Button variant="outline" role="radio" aria-checked={selected} onClick={onClick} className={choiceCardClass(selected)}>
      <Icon icon={selected ? "lucide:circle-dot" : "lucide:circle"} className={cn("h-4 w-4 shrink-0", selected ? "text-[#E4002B]" : "text-[#9CA3AF]")} />
      {label}
    </Button>
  );
}

const asText = (v: unknown) => (v === undefined || v === null ? "" : String(v));

// ── Panel ────────────────────────────────────────────────────────────────────

interface DecisionPanelProps {
  item: PhysicianReviewItem;
  /** A resolving decision was recorded — leave the screen. */
  onResolved: () => void;
  /** The item changed server-side (question sent, or found not decidable) — show the new state. */
  onItemChanged: (item: PhysicianReviewItem | null) => void;
  onNotFound: () => void;
  /** Shown on the same line as the submit button (the screen's "Release to queue"). */
  secondaryAction?: ReactNode;
}

export function DecisionPanel({ item, onResolved, onItemChanged, onNotFound, secondaryAction }: DecisionPanelProps) {
  const existing = item.records.find((r) => r.recordRole === "EXISTING");
  const incoming = item.records.find((r) => r.recordRole === "INCOMING");
  const isPair = Boolean(existing && incoming);
  const options = isPair ? PAIR_OPTIONS : SINGLE_OPTIONS;

  const [action, setAction] = useState<DecisionAction | null>(null);
  const [targetRole, setTargetRole] = useState<Role | null>(null);
  const [edits, setEdits] = useState<Partial<Record<CorrectableField, string>>>({});
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const needsTarget = action === "MARK_DISCONTINUED" || action === "CORRECT_VALUE";
  const target = isPair
    ? (targetRole === "EXISTING" ? existing : targetRole === "INCOMING" ? incoming : undefined)
    : item.records[0];

  // The chosen record's current values — the correction form starts from these.
  const original = useMemo(() => {
    const f = target?.fields ?? {};
    return Object.fromEntries(CORRECTABLE_FIELDS.map((k) => [k, asText(f[k])])) as Record<CorrectableField, string>;
  }, [target]);

  const values = { ...original, ...edits };
  // Only fields that differ from the record AND aren't blank are sent.
  const changed: CorrectedValue = Object.fromEntries(
    CORRECTABLE_FIELDS.filter((k) => values[k].trim() !== "" && values[k].trim() !== original[k].trim()).map((k) => [
      k,
      k === "dose_quantity" ? Number(values[k]) : values[k].trim(),
    ]),
  );
  const hasChanges = Object.keys(changed).length > 0;

  const valid =
    action !== null &&
    reason.trim().length > 0 &&
    (!needsTarget || !isPair || targetRole !== null) &&
    (action !== "CORRECT_VALUE" || hasChanges);

  const choose = (a: DecisionAction) => {
    setAction(a);
    setFormError(null);
    if (a !== "MARK_DISCONTINUED" && a !== "CORRECT_VALUE") setTargetRole(null);
    setEdits({});
  };

  const send = async () => {
    if (!action) return;
    setSending(true);
    const { data, error } = await decideReviewItemApi(item.id, {
      action,
      reason: reason.trim(),
      ...(needsTarget && target ? { targetRecordId: target.id } : {}),
      ...(action === "CORRECT_VALUE" ? { correctedValue: changed } : {}),
    });
    setSending(false);
    if (data) {
      if (action === "REQUEST_MORE_INFO") {
        toastSuccess(MESSAGES.questionSent);
        onItemChanged(data);
      } else {
        toastSuccess(MESSAGES.decided);
        onResolved();
      }
      return;
    }
    if (error?.code === "ITEM_NOT_DECIDABLE") {
      toastError(MESSAGES.notDecidable);
      onItemChanged(null);
    } else if (error?.code === "REVIEW_ITEM_NOT_FOUND") {
      onNotFound();
    } else if (error?.code === "VALIDATION_ERROR") {
      setFormError(error.message);
    } else {
      toastError(error?.message ?? "Unable to record the decision.");
    }
  };

  if (item.itemStatus === "NEEDS_MORE_INFO") {
    const question = [...(item.physicianActions ?? [])].reverse().find((a) => a.action === "REQUEST_MORE_INFO");
    return (
      <section aria-label="Decision" className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
        <p className="text-sm font-extrabold text-[#0A2947]">Decision</p>
        <div className="mt-3 rounded-2xl border border-[#F9CA39]/60 bg-[#FEF6DC] p-3">
          <p className="flex items-center gap-1.5 text-xs font-extrabold text-[#946200]">
            <Icon icon="lucide:hourglass" className="h-3.5 w-3.5" /> {MESSAGES.waiting}
          </p>
          {question && (
            <>
              <p className="mt-2 text-[11px] text-[#6B7280]">You asked:</p>
              <p className="!mb-0 text-sm text-[#374151]">"{question.reason}"</p>
            </>
          )}
        </div>
      </section>
    );
  }

  if (item.itemStatus !== "IN_PHYSICIAN_REVIEW") {
    return (
      <section aria-label="Decision" className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
        <p className="text-sm font-extrabold text-[#0A2947]">Decision</p>
        <p className="mt-1 text-xs text-[#9CA3AF]">Decisions aren't available for this item.</p>
      </section>
    );
  }

  const isQuestion = action === "REQUEST_MORE_INFO";

  return (
    <section aria-label="Decision" className="grid gap-4 rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      <p className="text-sm font-extrabold text-[#0A2947]">Decision</p>

      <div role="radiogroup" aria-label="Decision" className="grid gap-2">
        {options.map((o) => (
          <Choice key={o.action} selected={action === o.action} label={o.label} onClick={() => choose(o.action)} />
        ))}
      </div>

      {needsTarget && isPair && (
        <div>
          <p id="decision-target" className="mb-2 text-xs font-extrabold text-[#0A2947]">Which record? <span className="text-[#E4002B]">*</span></p>
          <div role="radiogroup" aria-labelledby="decision-target" className="grid grid-cols-2 gap-2">
            {(["EXISTING", "INCOMING"] as Role[]).map((role) => (
              <Choice
                key={role}
                selected={targetRole === role}
                label={role === "EXISTING" ? "Earlier record" : "Newer record"}
                onClick={() => { setTargetRole(role); setEdits({}); }}
              />
            ))}
          </div>
        </div>
      )}

      {action === "CORRECT_VALUE" && target && (
        <div className="grid gap-3 rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3" aria-label="Correct the value" role="group">
          <p className="text-xs font-extrabold text-[#0A2947]">Corrected values</p>
          {CORRECTABLE_FIELDS.map((k) => (
            <label key={k} className="grid gap-1">
              <span className="text-[11px] text-[#6B7280]">{FIELD_LABELS[k]}</span>
              {k === "status" ? (
                <Select
                  aria-label={FIELD_LABELS[k]}
                  value={values.status || undefined}
                  onChange={(v: string) => setEdits((e) => ({ ...e, status: v }))}
                  options={[...new Set([...STATUS_OPTIONS, ...(original.status ? [original.status] : [])])].map((s) => ({ value: s, label: s }))}
                  className="!w-full"
                />
              ) : k === "dose_quantity" ? (
                <InputNumber
                  aria-label={FIELD_LABELS[k]}
                  value={values.dose_quantity === "" ? null : Number(values.dose_quantity)}
                  onChange={(v) => setEdits((e) => ({ ...e, dose_quantity: v === null || v === undefined ? "" : String(v) }))}
                  className="!w-full !rounded-lg"
                />
              ) : (
                <Input
                  aria-label={FIELD_LABELS[k]}
                  value={values[k]}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [k]: e.target.value }))}
                  className="!rounded-lg !text-sm"
                />
              )}
            </label>
          ))}
          {!hasChanges && <p className="!mb-0 text-[11px] text-[#9CA3AF]">Change at least one value.</p>}
        </div>
      )}

      {action && (
        <div>
          <label htmlFor="decision-reason" className="mb-2 block text-xs font-extrabold text-[#0A2947]">
            {isQuestion ? MESSAGES.questionLabel : MESSAGES.reasonLabel}
          </label>
          <Textarea
            id="decision-reason"
            value={reason}
            onChange={(e) => { setReason(e.target.value.slice(0, REASON_MAX_LENGTH)); setFormError(null); }}
            maxLength={REASON_MAX_LENGTH}
            showCount
            rows={4}
            className="!rounded-lg"
          />
        </div>
      )}

      {formError && <p role="alert" className="!mb-0 text-xs text-[#E4002B]">{formError}</p>}

      {/* What it will do, said plainly — then recorded straight away (no second confirm). */}
      {valid && action && (
        <p className="!mb-0 text-xs text-[#4B5563]" data-testid="decision-effect">
          {effectSentence(action, isPair, targetRole ?? undefined)}
          {!isQuestion && " The item will be closed and the patient notified."}
        </p>
      )}
      <div className="flex gap-3">
        {secondaryAction && <div className="flex-1 [&>button]:!w-full">{secondaryAction}</div>}
        <Button disabled={!valid} loading={sending} onClick={() => void send()} className={cn(BRAND, "!w-full !flex-1")}>
          {isQuestion ? "Send question" : "Record decision"}
        </Button>
      </div>

    </section>
  );
}
